import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { catchError, tap, of, finalize, Subscription } from 'rxjs';
import moment from 'moment-timezone';

import { PricePlan } from '@shared/price-plan/models';
import { RecurlySubscription } from '@shared/recurly/models';
import { SubscriptionService } from '../../services';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';

@Component({
  selector: 'pause-or-cancel-subscription',
  templateUrl: './pause-subscription.component.html',
})
export class PauseSubscriptionComponent implements OnInit, OnDestroy {
  @Input() pricePlan?: PricePlan;
  @Input() timezone?: string;
  @Input() sub?: RecurlySubscription;
  @Input() onClose?: () => void;

  inProgress = false;
  pauseCyclesForm: UntypedFormGroup;
  pauseText: string;
  isCurrentSubscriptionOnPremiumPlan = false;
  private readonly subscriptions = new Subscription();

  constructor(
    readonly modalController: FrontendModalService,
    private readonly alertsService: AlertsService,
    private readonly subscriptionService: SubscriptionService,
    private readonly fb: UntypedFormBuilder,
  ) {}

  ngOnInit(): void {
    this.isCurrentSubscriptionOnPremiumPlan = this.sub.plan_code.includes('premium');
    this.pauseCyclesForm = this.fb.group({
      pauseCycles: [1, Validators.compose([Validators.required, Validators.min(1)])],
    });
    this.pauseCyclesChanged(1);
    const changePauseCyclesSub = this.pauseCyclesForm.get('pauseCycles').valueChanges.subscribe(pauseCycles => this.pauseCyclesChanged(pauseCycles));
    this.subscriptions.add(changePauseCyclesSub);
  }

  cancel(): void {
    this.modalController.dismiss();
    if (typeof this.onClose === 'function') {
      this.onClose();
    }
  }

  pause(): void {
    this.subscriptionService.pause({
      uuid: this.sub.uuid,
      pauseCycles: this.pauseCyclesForm.get('pauseCycles').value.toString(),
    })
      .pipe(
        tap(() => {
          this.alertsService.add({
            type: IAlertTypes.success,
            heading: 'Success:',
            body: 'subscription paused successfully.',
          });
          this.modalController.dismiss();
        }),
        catchError((error) => {
          this.alertsService.add({
            type: IAlertTypes.danger,
            heading: 'Error:',
            body: String(error),
          });
          this.modalController.dismiss();

          return of(error);
        }),
        finalize(() => {
          if (typeof this.onClose === 'function') {
            this.onClose();
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private pauseCyclesChanged(pauseCycles: number): void {
    const expiration = moment(this.sub.expiration_date).tz(this.timezone);
    let duration = 1;
    if (this.pricePlan) {
      duration = this.pricePlan.plan_interval_length;
    } else if (this.pricePlan.plan_code.includes('annual')) {
      duration = 12; // eslint-disable-line no-magic-numbers
    }
    const restart = expiration.clone().add(duration * pauseCycles, 'M');
    this.pauseText = `Your subscription will pause from ${expiration.format('L')} and will resume automatically on ${restart.format('L')}.`;
  }
}
